/* Build Your Pack (byp): tabbed accessory builder for the Be Yours main product section.
 * - Tabs (ARIA tablist), scroll-snap carousels with prev/next arrows
 * - Card variant dropdowns, Add / Added toggle, "Your Build" summary with live total
 * - Adds the main product + accessories in one /cart/add.js request, re-renders the cart
 *   sections the same way cart.js does (by element id) and opens the theme's cart drawer
 * The theme's product-form.js is untouched: the builder intercepts the submit event of the
 * main / sticky product forms in the capture phase, before <product-form> receives it.
 */
(() => {
  if (customElements.get('build-your-pack')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const stripTags = (value) => String(value).replace(/<[^>]*>/g, '');

  class BuildYourPack extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('script[data-byp-config]');
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        console.error('[build-your-pack] Invalid config', error);
        return;
      }

      this.strings = this.config.strings || {};
      this.selections = new Map();
      this.busy = false;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[data-byp-panel]'));
      this.chipsList = this.querySelector('[data-byp-chips]');
      this.chipsWrap = this.querySelector('[data-byp-chips-wrap]');
      this.chipsPrev = this.querySelector('[data-byp-chips-prev]');
      this.chipsNext = this.querySelector('[data-byp-chips-next]');
      this.mainChip = this.querySelector('[data-byp-main-chip]');
      this.countEl = this.querySelector('[data-byp-count]');
      this.totalEl = this.querySelector('[data-byp-total]');
      this.cta = this.querySelector('[data-byp-cta]');
      this.ctaLabel = this.querySelector('[data-byp-cta-label]');
      this.errorEl = this.querySelector('[data-byp-error]');
      this.errorText = this.querySelector('[data-byp-error-text]');

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onResize = () => this.updateArrows(this.activePanel);
      this.onChipsScroll = () => {
        if (this.chipsFrame) return;
        this.chipsFrame = requestAnimationFrame(() => {
          this.chipsFrame = null;
          this.updateChipArrows();
        });
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      this.panels.forEach((panel) => {
        panel.querySelector('[data-byp-track]')?.addEventListener('scroll', this.onTrackScroll, { passive: true });
      });

      this.chipsList?.addEventListener('scroll', this.onChipsScroll, { passive: true });
      this.chipsObserver = new ResizeObserver(this.onChipsScroll);
      if (this.chipsList) this.chipsObserver.observe(this.chipsList);

      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('submit', this.onDocumentSubmit, true);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.resizeObserver = new ResizeObserver(this.onResize);

      this.initCards();
      this.initTabs();
      this.toggleThemeButton(true);
      this.readMain();
      this.render();
    }

    disconnectedCallback() {
      if (!this.config) return;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      this.panels.forEach((panel) => {
        panel.querySelector('[data-byp-track]')?.removeEventListener('scroll', this.onTrackScroll);
      });
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.chipsList?.removeEventListener('scroll', this.onChipsScroll);
      this.chipsObserver?.disconnect();
      if (this.chipsFrame) cancelAnimationFrame(this.chipsFrame);
      this.toggleThemeButton(false);
    }

    /* ---------- Main product (theme product form) ---------- */

    get mainForm() {
      return document.getElementById(this.config.mainFormId);
    }

    get mainProductForm() {
      return this.mainForm?.closest('product-form') || null;
    }

    get themeSubmitButton() {
      return document.getElementById(`ProductSubmitButton-${this.config.sectionId}`);
    }

    toggleThemeButton(hide) {
      if (!this.config.useAsMainAtc) return;
      this.themeSubmitButton?.classList.toggle('byp-atc-hidden', hide);
    }

    getQuantityInput(form) {
      if (!form) return null;
      return form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${form.getAttribute('id')}"]`);
    }

    readMain() {
      const form = this.mainForm;
      const idInput = form?.querySelector('[name="id"]');
      const variantId = Number(idInput?.value);
      const variants = this.config.main.variants || [];
      const variant = variants.find((v) => v.id === variantId) || null;

      const qtyInput = this.getQuantityInput(form);
      const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

      this.main = {
        variant,
        quantity,
        available: Boolean(variant && variant.available && idInput && !idInput.disabled),
      };
    }

    onDocumentChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !this.mainForm) return;
      if (target.form !== this.mainForm) return;
      if (target.name !== 'id' && target.name !== 'quantity') return;
      this.readMain();
      this.render();
    }

    /* ---------- Tabs ---------- */

    initTabs() {
      if (!this.tabs.length) return;
      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      this.activateTab(selected || this.tabs[0], { focus: false });
    }

    get activePanel() {
      return this.panels.find((panel) => !panel.hidden) || null;
    }

    activateTab(tab, { focus = true } = {}) {
      if (!tab) return;
      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.setAttribute('aria-selected', String(isActive));
        item.tabIndex = isActive ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });

      const panel = this.activePanel;
      const track = panel?.querySelector('[data-byp-track]');
      if (track) {
        track.scrollLeft = 0;
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(track);
      }
      this.updateArrows(panel);
      if (focus) tab.focus();
    }

    onKeydown(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      const index = this.tabs.indexOf(tab);
      const isRtl = getComputedStyle(this).direction === 'rtl';
      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = this.tabs[(index + (isRtl ? -1 : 1) + this.tabs.length) % this.tabs.length];
          break;
        case 'ArrowLeft':
          next = this.tabs[(index + (isRtl ? 1 : -1) + this.tabs.length) % this.tabs.length];
          break;
        case 'Home':
          next = this.tabs[0];
          break;
        case 'End':
          next = this.tabs[this.tabs.length - 1];
          break;
        default:
          return;
      }
      event.preventDefault();
      this.activateTab(next);
    }

    onBlockSelect(event) {
      if (event.detail?.sectionId !== this.config.sectionId) return;
      const tab = this.tabs.find((item) => item.dataset.blockId === event.detail.blockId);
      if (tab) this.activateTab(tab, { focus: false });
    }

    /* ---------- Carousel ---------- */

    onTrackScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateArrows(this.activePanel);
      });
    }

    getStep(track) {
      const card = track.firstElementChild;
      if (!card) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    updateArrows(panel) {
      if (!panel) return;
      const track = panel.querySelector('[data-byp-track]');
      const prev = panel.querySelector('[data-byp-prev]');
      const next = panel.querySelector('[data-byp-next]');
      if (!track) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      const position = Math.abs(track.scrollLeft);
      const isStatic = maxScroll <= 1;
      panel.classList.toggle('byp__panel--static', isStatic);
      if (isStatic) return;

      this.setDisabled(prev, position <= 1, next, track);
      this.setDisabled(next, position >= maxScroll - 1, prev, track);
    }

    // Move focus off a button before disabling it so keyboard focus is not lost.
    setDisabled(button, disabled, fallback, track) {
      if (!button || button.disabled === disabled) return;
      if (disabled && document.activeElement === button) {
        (fallback && !fallback.disabled ? fallback : track).focus({ preventScroll: true });
      }
      button.disabled = disabled;
    }

    scrollTrack(panel, direction) {
      const track = panel?.querySelector('[data-byp-track]');
      if (!track) return;
      const step = this.getStep(track);
      if (!step) return;
      const perPage = Math.max(1, Math.floor((track.clientWidth + 1) / step));
      const isRtl = getComputedStyle(track).direction === 'rtl';
      track.scrollBy({
        left: direction * step * perPage * (isRtl ? -1 : 1),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    /* ---------- Cards ---------- */

    initCards() {
      this.cards = Array.from(this.querySelectorAll('[data-byp-card]'));
      this.cards.forEach((card) => {
        try {
          card.bypVariants = JSON.parse(card.querySelector('script[data-byp-variants]')?.textContent || '[]');
        } catch (error) {
          card.bypVariants = [];
        }
        const select = card.querySelector('[data-byp-variant-select]');
        const variantId = select ? Number(select.value) : card.bypVariants[0]?.id;
        card.bypVariant = card.bypVariants.find((v) => v.id === variantId) || card.bypVariants[0] || null;
      });
    }

    cardsForKey(key) {
      return this.cards.filter((card) => card.dataset.key === key);
    }

    updateCard(card) {
      const variant = card.bypVariant;
      if (!variant) return;

      const price = card.querySelector('[data-byp-price]');
      const compare = card.querySelector('[data-byp-compare]');
      const compareValue = card.querySelector('[data-byp-compare-value]');
      const button = card.querySelector('[data-byp-add]');
      const image = card.querySelector('.byp-card__image');
      const select = card.querySelector('[data-byp-variant-select]');

      if (price) price.textContent = this.formatMoney(variant.price);
      if (compare) {
        const showCompare = this.config.showCompareAt && variant.compare > variant.price;
        compare.hidden = !showCompare;
        if (showCompare && compareValue) compareValue.textContent = this.formatMoney(variant.compare);
      }
      if (select && Number(select.value) !== variant.id) select.value = String(variant.id);
      if (image && image.tagName === 'IMG' && variant.image && image.getAttribute('src') !== variant.image) {
        if (variant.srcset) image.srcset = variant.srcset;
        image.src = variant.image;
      }

      const selected = this.selections.has(card.dataset.key);
      card.classList.toggle('byp-card--sold-out', !variant.available);
      card.classList.toggle('byp-card--selected', selected);
      if (button) {
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', String(selected && variant.available));
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-byp-variant-select]');
      if (!select) return;
      const card = select.closest('[data-byp-card]');
      const variant = card?.bypVariants.find((v) => v.id === Number(select.value));
      if (!variant) return;

      const key = card.dataset.key;
      this.cardsForKey(key).forEach((item) => {
        item.bypVariant = item.bypVariants.find((v) => v.id === variant.id) || item.bypVariant;
      });

      const selection = this.selections.get(key);
      if (selection) {
        if (variant.available) {
          this.selections.set(key, this.buildSelection(card, variant, selection.quantity));
        } else {
          this.selections.delete(key);
        }
      }

      this.cardsForKey(key).forEach((item) => this.updateCard(item));
      this.hideError();
      this.render();
    }

    buildSelection(card, variant, quantity = 1) {
      const hasOnlyDefault = card.bypVariants.length === 1 && variant.title === 'Default Title';
      return {
        key: card.dataset.key,
        variantId: variant.id,
        title: card.dataset.title,
        variantTitle: hasOnlyDefault ? '' : variant.title,
        price: variant.price,
        image: variant.thumb,
        quantity,
      };
    }

    toggleCard(card) {
      const key = card.dataset.key;
      const variant = card.bypVariant;
      let added = false;
      if (this.selections.has(key)) {
        this.selections.delete(key);
      } else if (variant && variant.available) {
        this.selections.set(key, this.buildSelection(card, variant));
        added = true;
      }
      this.cardsForKey(key).forEach((item) => this.updateCard(item));
      this.hideError();
      this.render();

      // Bring the newly added item into view in the Your Build row.
      if (added && this.chipsList) {
        requestAnimationFrame(() => {
          const isRtl = getComputedStyle(this.chipsList).direction === 'rtl';
          this.chipsList.scrollTo({
            left: isRtl ? -this.chipsList.scrollWidth : this.chipsList.scrollWidth,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        });
      }
    }

    removeSelection(key) {
      this.selections.delete(key);
      this.cardsForKey(key).forEach((item) => this.updateCard(item));
      this.render();
    }

    onClick(event) {
      const add = event.target.closest('[data-byp-add]');
      if (add) {
        const card = add.closest('[data-byp-card]');
        if (card) this.toggleCard(card);
        return;
      }

      // Tapping the image toggles the item too (pointer shortcut; the button stays the keyboard control).
      const media = event.target.closest('[data-byp-media]');
      if (media) {
        const card = media.closest('[data-byp-card]');
        if (card && !card.querySelector('[data-byp-add]')?.disabled) this.toggleCard(card);
        return;
      }

      const tab = event.target.closest('[role="tab"]');
      if (tab) {
        this.activateTab(tab, { focus: false });
        return;
      }

      const chipsPrev = event.target.closest('[data-byp-chips-prev]');
      const chipsNext = event.target.closest('[data-byp-chips-next]');
      if (chipsPrev || chipsNext) {
        this.scrollChips(chipsPrev ? -1 : 1);
        return;
      }

      const prev = event.target.closest('[data-byp-prev]');
      const next = event.target.closest('[data-byp-next]');
      if (prev || next) {
        this.scrollTrack((prev || next).closest('[data-byp-panel]'), prev ? -1 : 1);
        return;
      }

      const remove = event.target.closest('[data-byp-remove]');
      if (remove) {
        const chip = remove.closest('[data-key]');
        const list = remove.closest('[data-byp-chips]');
        const chips = Array.from(list.querySelectorAll('[data-byp-remove]'));
        const index = chips.indexOf(remove);
        this.removeSelection(chip.dataset.key);
        // Keep keyboard focus in the summary after the chip is removed.
        const remaining = Array.from(list.querySelectorAll('[data-byp-remove]'));
        (remaining[Math.min(index, remaining.length - 1)] || this.cta)?.focus({ preventScroll: true });
        return;
      }

      const step = event.target.closest('[data-byp-qty]');
      if (step) {
        const chip = step.closest('[data-key]');
        const selection = this.selections.get(chip?.dataset.key);
        if (!selection) return;
        const quantity = selection.quantity + Number(step.dataset.bypQty);
        if (quantity < 1) {
          this.removeSelection(selection.key);
          this.cta?.focus({ preventScroll: true });
          return;
        }
        selection.quantity = quantity;
        this.render();
        this.chipsList.querySelector(`[data-key="${selection.key}"] [data-byp-qty="${step.dataset.bypQty}"]`)?.focus({ preventScroll: true });
      }
    }

    /* ---------- Summary ---------- */

    formatMoney(cents) {
      const format = this.config.moneyFormat || window.theme?.shopSettings?.moneyFormat;
      if (window.theme?.Currency?.formatMoney && format) {
        return stripTags(theme.Currency.formatMoney(cents, format));
      }
      const currency = window.theme?.shopSettings?.isoCode || window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    }

    get totals() {
      const mainPrice = this.main.variant ? this.main.variant.price * this.main.quantity : 0;
      let total = mainPrice;
      let count = this.main.quantity;
      this.selections.forEach((selection) => {
        total += selection.price * selection.quantity;
        count += selection.quantity;
      });
      return { total, count };
    }

    render() {
      if (!this.main) return;
      const { total, count } = this.totals;
      const totalText = this.formatMoney(total);

      if (this.countEl) {
        this.countEl.textContent = `${count} ${count === 1 ? this.strings.item : this.strings.items}`;
      }
      if (this.totalEl) this.totalEl.textContent = totalText;

      this.renderMainChip();
      this.renderChips();

      if (this.cta && !this.busy) {
        this.cta.disabled = !this.main.available;
        this.ctaLabel.textContent = this.main.available
          ? (this.strings.cta || '').replace('[total]', totalText)
          : this.strings.soldOut;
      }
    }

    renderMainChip() {
      if (!this.mainChip) return;
      const variant = this.main.variant;
      const variantEl = this.mainChip.querySelector('[data-byp-main-variant]');
      const priceEl = this.mainChip.querySelector('[data-byp-main-price]');
      const media = this.mainChip.querySelector('.byp__chip-media');

      if (variantEl) {
        variantEl.textContent = variant && !this.config.main.hasOnlyDefaultVariant ? variant.title : '';
      }
      if (priceEl) {
        const unit = variant ? this.formatMoney(variant.price) : '';
        priceEl.textContent = this.main.quantity > 1 ? `${this.main.quantity} × ${unit}` : unit;
      }
      if (media && variant?.image) {
        let img = media.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.alt = '';
          img.width = 40;
          img.height = 40;
          media.appendChild(img);
        }
        if (img.getAttribute('src') !== variant.image) {
          img.removeAttribute('srcset');
          img.src = variant.image;
        }
      }
    }

    renderChips() {
      if (!this.chipsList) return;
      // Only list the build (pack + add-ons) once at least one add-on is selected.
      (this.chipsWrap || this.chipsList).hidden = this.selections.size === 0;
      this.chipsList.querySelectorAll('[data-byp-addon-chip]').forEach((chip) => chip.remove());
      const fragment = document.createDocumentFragment();
      this.selections.forEach((selection) => fragment.appendChild(this.createChip(selection)));
      this.chipsList.appendChild(fragment);
      requestAnimationFrame(() => this.updateChipArrows());
    }

    // Arrows for the Your Build row: same behaviour as the card carousel arrows.
    updateChipArrows() {
      const list = this.chipsList;
      if (!list || !this.chipsWrap) return;
      const maxScroll = list.scrollWidth - list.clientWidth;
      const position = Math.abs(list.scrollLeft);
      const isStatic = maxScroll <= 1;
      this.chipsWrap.classList.toggle('byp__chips-wrap--static', isStatic);
      if (isStatic) return;
      this.setDisabled(this.chipsPrev, position <= 1, this.chipsNext, list);
      this.setDisabled(this.chipsNext, position >= maxScroll - 1, this.chipsPrev, list);
    }

    scrollChips(direction) {
      const list = this.chipsList;
      if (!list) return;
      const isRtl = getComputedStyle(list).direction === 'rtl';
      list.scrollBy({
        left: direction * Math.max(list.clientWidth * 0.8, 120) * (isRtl ? -1 : 1),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    createChip(selection) {
      const chip = document.createElement('li');
      chip.className = 'byp__chip';
      chip.dataset.key = selection.key;
      chip.setAttribute('data-byp-addon-chip', '');

      const label = selection.variantTitle ? `${selection.title} – ${selection.variantTitle}` : selection.title;

      const media = document.createElement('div');
      media.className = 'byp__chip-media';
      if (selection.image) {
        const img = document.createElement('img');
        img.src = selection.image;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const body = document.createElement('div');
      body.className = 'byp__chip-body';
      const title = document.createElement('span');
      title.className = 'byp__chip-title';
      title.textContent = selection.title;
      body.appendChild(title);
      if (selection.variantTitle) {
        const variant = document.createElement('span');
        variant.className = 'byp__chip-variant';
        variant.textContent = selection.variantTitle;
        body.appendChild(variant);
      }
      const price = document.createElement('span');
      price.className = 'byp__chip-price';
      const unit = this.formatMoney(selection.price);
      price.textContent = selection.quantity > 1 && !this.config.enableQuantity ? `${selection.quantity} × ${unit}` : unit;
      body.appendChild(price);

      chip.append(media, body);

      if (this.config.enableQuantity) {
        const stepper = document.createElement('div');
        stepper.className = 'byp__chip-qty';
        const minus = this.createButton('byp__qty-btn', '−', (this.strings.decrease || '').replace('[product]', label));
        minus.dataset.bypQty = '-1';
        const value = document.createElement('span');
        value.className = 'byp__qty-value';
        value.textContent = String(selection.quantity);
        value.setAttribute('aria-label', `${this.strings.quantity}: ${selection.quantity}`);
        const plus = this.createButton('byp__qty-btn', '+', (this.strings.increase || '').replace('[product]', label));
        plus.dataset.bypQty = '1';
        stepper.append(minus, value, plus);
        chip.appendChild(stepper);
      }

      const remove = this.createButton('byp__chip-remove', '×', (this.strings.remove || '').replace('[title]', label));
      remove.setAttribute('data-byp-remove', '');
      chip.appendChild(remove);

      return chip;
    }

    createButton(className, text, ariaLabel) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.setAttribute('aria-label', ariaLabel);
      const glyph = document.createElement('span');
      glyph.setAttribute('aria-hidden', 'true');
      glyph.textContent = text;
      button.appendChild(glyph);
      return button;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorText.textContent = message || this.strings.error;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* ---------- Add to cart ---------- */

    onDocumentSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // form.id is shadowed by the theme's <input name="id">, so read the attribute.
      const formId = form.getAttribute('id');
      const isMain = formId === this.config.mainFormId;
      const isSticky = formId === this.config.stickyFormId;
      if (!isMain && !isSticky) return;

      const fromCta = Boolean(event.submitter && this.contains(event.submitter));
      const intercept =
        fromCta ||
        (isMain && this.config.useAsMainAtc) ||
        (isSticky && (this.config.useAsMainAtc || this.selections.size > 0));
      if (!intercept) return;

      // Stop the theme's <product-form> submit handler from also adding the product.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addBuild(event);
    }

    getMainLineItem() {
      const form = this.mainForm;
      const item = { id: this.main.variant.id, quantity: this.main.quantity };
      if (!form) return item;

      const formData = new FormData(form);
      const properties = {};
      formData.forEach((value, name) => {
        if (name === 'selling_plan' && value) item.selling_plan = Number(value);
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;
      return item;
    }

    buildItems() {
      const buildId = this.config.buildProperty ? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}` : null;
      const withBuild = (item) => {
        if (!buildId) return item;
        return { ...item, properties: { ...(item.properties || {}), _build: buildId } };
      };

      const main = this.getMainLineItem();
      const items = [{ line: withBuild(main), title: this.config.main.title, key: null }];
      this.selections.forEach((selection) => {
        items.push({
          line: withBuild({ id: selection.variantId, quantity: selection.quantity }),
          title: selection.variantTitle ? `${selection.title} – ${selection.variantTitle}` : selection.title,
          key: selection.key,
        });
      });
      return items;
    }

    get cartAddUrl() {
      const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
      return `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;
    }

    get miniCart() {
      return document.querySelector('mini-cart');
    }

    // Cart sections to refresh, built like cart.js getSectionsToRender(): every cart element
    // that exists on the page (by id), so it works even if <mini-cart> is not upgraded.
    // Section Rendering API allows max 5 sections per request.
    get sectionIds() {
      const fromTheme =
        typeof this.miniCart?.getSectionsToRender === 'function'
          ? this.miniCart.getSectionsToRender().map((section) => section.id)
          : [];
      const ids = [...fromTheme, 'mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble', 'cart-live-region-text'];
      return [...new Set(ids)].filter((id) => document.getElementById(id)).slice(0, 5);
    }

    // Same rendering as cart.js renderContents(): replace each element with the fresh section HTML.
    renderCartSections(sections) {
      let rendered = 0;
      Object.entries(sections || {}).forEach(([id, html]) => {
        const element = document.getElementById(id);
        if (!element || typeof html !== 'string') return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('.shopify-section') || parsed.body;
        if (!source) return;
        element.innerHTML = source.innerHTML;
        rendered += 1;
      });
      return rendered;
    }

    // Open the theme's cart drawer: <mini-cart>.open() when cart-drawer.js is loaded,
    // otherwise <cart-drawer>.openMenuDrawer() from global.js.
    openCartDrawer() {
      const miniCart = this.miniCart;
      if (miniCart && typeof miniCart.open === 'function' && miniCart.drawer) {
        miniCart.open();
        return true;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.openMenuDrawer === 'function') {
        if (!drawer.querySelector('details')?.hasAttribute('open')) drawer.openMenuDrawer();
        return true;
      }
      return false;
    }

    postItems(lines, withSections) {
      const body = { items: lines };
      if (withSections && this.sectionIds.length) {
        body.sections = this.sectionIds;
        body.sections_url = window.location.pathname;
      }
      return fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      }).then((response) => response.json().then((data) => ({ ok: response.ok && !data.status, data })));
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.cta) return;
      this.cta.disabled = busy || !this.main.available;
      this.cta.setAttribute('aria-busy', String(busy));
      this.cta.classList.toggle('byp__cta--loading', busy);
    }

    async addBuild(event) {
      if (this.busy) return;
      this.readMain();
      this.hideError();

      if (!this.main.available) {
        this.showError(this.strings.soldOut);
        return;
      }

      // Reuse the theme's required line item property check (and its error message).
      const productForm = this.mainProductForm;
      if (productForm && typeof productForm.blockSubmitIfRequiredFieldsEmpty === 'function') {
        if (!productForm.blockSubmitIfRequiredFieldsEmpty(event)) {
          this.showError(window.theme?.accessibilityStrings?.fillInAllLineItemPropertyRequiredFields);
          return;
        }
      }

      const items = this.buildItems();
      this.miniCart?.setActiveElement?.(document.activeElement);
      this.setBusy(true);

      try {
        const result = await this.postItems(items.map((item) => item.line), true);
        if (result.ok) {
          await this.onAdded(result.data, items);
          if (this.config.clearAfterAdd) this.clearSelections();
        } else {
          await this.retryIndividually(items, result.data);
        }
      } catch (error) {
        console.error('[build-your-pack]', error);
        this.showError(this.strings.error);
      } finally {
        this.setBusy(false);
        this.render();
      }
    }

    // One item failed (sold out, inventory limit): add the rest one by one and report the failures.
    async retryIndividually(items, firstError) {
      const failed = [];
      const added = [];
      let lastMessage = firstError?.description || firstError?.message || '';

      for (const item of items) {
        try {
          const result = await this.postItems([item.line], false);
          if (result.ok) {
            added.push(item);
          } else {
            failed.push(item);
            lastMessage = result.data?.description || result.data?.message || lastMessage;
          }
        } catch (error) {
          failed.push(item);
        }
      }

      if (added.length) {
        const sections = await this.fetchSections();
        await this.onAdded({ sections, items: [] }, added);
        if (this.config.clearAfterAdd) {
          added.forEach((item) => item.key && this.selections.delete(item.key));
          this.cards.forEach((card) => this.updateCard(card));
        }
      }

      const names = failed.map((item) => item.title).join(', ');
      const partial = (this.strings.partialError || '').replace('[items]', names);
      this.showError([lastMessage, added.length && names ? partial : ''].filter(Boolean).join(' '));

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // Same event the theme's product form publishes on add errors.
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'build-your-pack',
          productVariantId: items[0]?.line.id,
          errors: lastMessage,
          message: lastMessage,
        });
      }
    }

    fetchSections() {
      const ids = this.sectionIds;
      if (!ids.length) return Promise.resolve({});
      const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
      return fetch(`${root.endsWith('/') ? root : `${root}/`}?sections=${ids.join(',')}`)
        .then((response) => response.json())
        .catch(() => ({}));
    }

    async onAdded(response, items) {
      this.dispatchEvent(
        new CustomEvent('byp:added', {
          bubbles: true,
          detail: { items: items.map((item) => item.line), response },
        })
      );

      // Fresh cart sections: from the add response, or fetched if the request had none.
      let state = response;
      if (!state.sections || !Object.keys(state.sections).length) {
        state = { ...response, sections: await this.fetchSections() };
      }

      // Render like cart.js renderContents(), then notify the theme the same way cart.js does.
      this.renderCartSections(state.sections);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'build-your-pack',
          productVariantId: items[0]?.line.id,
          cart: state,
        });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
      this.miniCart?.classList.remove('is-empty');

      if (this.openCartDrawer()) return;

      // Drawer components not upgraded: open it the way a customer would, via the header cart icon.
      const summary = document.querySelector('cart-drawer summary');
      if (summary) {
        if (!summary.parentElement?.hasAttribute('open')) summary.click();
        return;
      }

      // No cart drawer on the page at all: same fallback as the theme's product-form.js.
      console.warn('[build-your-pack] No cart drawer on this page. Going to the cart page.', {
        miniCartElement: Boolean(this.miniCart),
        cartDrawerElement: Boolean(document.querySelector('cart-drawer')),
        cartDrawerSetting: window.theme?.shopSettings?.cartDrawer,
      });
      window.location = window.theme?.routes?.cart_url || '/cart';
    }

    clearSelections() {
      this.selections.clear();
      this.cards.forEach((card) => this.updateCard(card));
    }
  }

  customElements.define('build-your-pack', BuildYourPack);
})();
